
import React, { useState } from "react";
import {
    MapPin,
    CalendarDays,
    Clock,
    Users,
    IndianRupee,
    GraduationCap,
    Upload,
    BriefcaseBusiness,
    Laptop,
    Mail,
    Phone,
    FileText,
    Send,
    CheckCircle2,
} from "lucide-react";
import { useEffect } from "react";
import "./TrainingOpportunities.css";

const initialForm = {
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    trainingTitle: "",
    skills: "",
    location: "",
    mode: "Offline / Classroom",
    startDate: "",
    duration: "",
    trainerExperience: "",
    budget: "",
    budgetType: "Monthly",
    tfa: "Not Covered",
    description: "",
};

export default function TrainingOpportunities() {
    const [form, setForm] = useState(initialForm);
    const [tocFile, setTocFile] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const opportunity = {
        title: "Corporate Trainer Requirement – AI Developer",
        company: "BitNestTech",
        location: "Hyderabad",
        locationNote: "Local trainers only",
        mode: "Offline / Classroom",
        startDate: "2026-10-30",
        duration: "30 Days",
        experience: "8+ Years",
        budget: "Monthly",
        tfa: "Not Covered",
        skills: "AI Development",
        phone: "9202364862 / 9302677001",
        email: "support@bitnesttech.com",
    };

    const updateField = (event) => {
        const { name, value } = event.target;
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const [opportunities, setOpportunities] = useState([]);
    const [loadingOpportunities, setLoadingOpportunities] = useState(true);
    const [fetchError, setFetchError] = useState("");

    const fetchOpportunities = async () => {
        try {
            setFetchError("");

            const response = await fetch(
                "/api/training-requirements"
            );

            if (!response.ok) {
                throw new Error("Unable to load training opportunities.");
            }

            const data = await response.json();
            setOpportunities(data);
        } catch (error) {
            setFetchError(error.message);
        } finally {
            setLoadingOpportunities(false);
        }
    };

    useEffect(() => {
        fetchOpportunities();
    }, []);


    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setMessage("");

        if (form.phone.trim().length < 10) {
            setError("Please enter a valid contact number.");
            return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();

            Object.entries(form).forEach(([key, value]) => {
                formData.append(key, value);
            });

            if (tocFile) {
                formData.append("tocFile", tocFile);
            }

            // Send form data to Spring Boot
            const response = await fetch(
                "/api/training-requirements",
                {
                    method: "POST",
                    body: formData,
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error || "Failed to submit training requirement."
                );
            }

            setMessage(
                "Training requirement submitted successfully!"
            );

            setForm(initialForm);
            setTocFile(null);
        } catch (err) {
            setError(
                err.message ||
                "Unable to submit the requirement. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <main className="training-page">
            <section className="training-hero">
                <div className="training-hero-content">
                    <span className="training-eyebrow">
                        CERTED TRAINING NETWORK
                    </span>

                    <h1>Training Opportunities</h1>

                    <p>
                        Discover corporate training assignments or submit your
                        organization's training requirements to connect with suitable
                        trainers.
                    </p>

                    <a href="#available-opportunities" className="training-primary-link">
                        Explore Opportunities
                    </a>
                </div>

                <div className="training-hero-icon">
                    <GraduationCap size={76} strokeWidth={1.4} />
                </div>
            </section>

            <section
                className="training-section"
                id="available-opportunities"
            >
                <div className="training-section-heading">
                    <div>
                        <span className="training-eyebrow">FOR TRAINERS</span>
                        <h2>Available Opportunity</h2>
                        <p>Review the requirement details before applying.</p>
                    </div>

                    <span className="training-count">1 Opportunity</span>
                </div>

                <section className="saved-opportunities">
                    <h2>Latest Training Opportunities</h2>
                    <p className="section-description">
                        Explore training requirements submitted by companies.
                    </p>

                    {loadingOpportunities && (
                        <p>Loading training opportunities...</p>
                    )}

                    {fetchError && (
                        <p className="opportunity-error">{fetchError}</p>
                    )}

                    {!loadingOpportunities &&
                        !fetchError &&
                        opportunities.length === 0 && (
                            <p>No additional opportunities available yet.</p>
                        )}

                    <div className="opportunity-grid">

                        {opportunities.map((item) => {
                            const opportunity = {
                                id: item.id,
                                title: item.trainingTitle,
                                company: item.companyName,
                                location: item.location || "Not specified",
                                locationNote: "",
                                mode: item.mode || "Not specified",
                                startDate: item.startDate,
                                duration: item.duration || "Not specified",
                                experience: item.trainerExperience || "Not specified",
                                budget:
                                    item.budget != null
                                        ? `₹${item.budget} ${item.budgetType || ""}`.trim()
                                        : item.budgetType || "Contact for details",
                                tfa: item.tfa || "Not specified",
                                skills: item.skills || "Not specified",
                                email: item.email || "",
                                phone: item.phone || "",
                                description: item.description || "",
                            };

                            const applicationSubject = `Application – ${opportunity.title}`;

                            return (
                                <article className="opportunity-card" key={opportunity.id}>
                                    <div className="opportunity-card-top">
                                        <div className="opportunity-company-icon">
                                            <BriefcaseBusiness size={27} />
                                        </div>

                                        <div className="opportunity-heading">
                                            <span className="opportunity-type">
                                                Corporate Training
                                            </span>
                                            <h3>{opportunity.title}</h3>
                                            <p>{opportunity.company}</p>
                                        </div>

                                        <span className="opportunity-status">
                                            Trainer Required
                                        </span>
                                    </div>

                                    <div className="opportunity-details">
                                        <div className="opportunity-detail">
                                            <MapPin />
                                            <div>
                                                <span>Location</span>
                                                <strong>{opportunity.location}</strong>
                                                {opportunity.locationNote && (
                                                    <small>{opportunity.locationNote}</small>
                                                )}
                                            </div>
                                        </div>

                                        <div className="opportunity-detail">
                                            <Laptop />
                                            <div>
                                                <span>Training Mode</span>
                                                <strong>{opportunity.mode}</strong>
                                            </div>
                                        </div>

                                        <div className="opportunity-detail">
                                            <CalendarDays />
                                            <div>
                                                <span>Start Date</span>
                                                <strong>
                                                    {opportunity.startDate
                                                        ? new Date(
                                                            `${opportunity.startDate}T00:00:00`
                                                        ).toLocaleDateString("en-IN", {
                                                            day: "2-digit",
                                                            month: "long",
                                                            year: "numeric",
                                                        })
                                                        : "To be announced"}
                                                </strong>
                                            </div>
                                        </div>

                                        <div className="opportunity-detail">
                                            <Clock />
                                            <div>
                                                <span>Duration</span>
                                                <strong>{opportunity.duration}</strong>
                                            </div>
                                        </div>

                                        <div className="opportunity-detail">
                                            <Users />
                                            <div>
                                                <span>Experience Required</span>
                                                <strong>{opportunity.experience}</strong>
                                            </div>
                                        </div>

                                        <div className="opportunity-detail">
                                            <IndianRupee />
                                            <div>
                                                <span>Budget</span>
                                                <strong>{opportunity.budget}</strong>
                                                <small>TFA: {opportunity.tfa}</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="opportunity-skills">
                                        <strong>Required Skills</strong>
                                        <span>{opportunity.skills}</span>
                                    </div>

                                    {opportunity.description && (
                                        <p className="opportunity-description">
                                            {opportunity.description}
                                        </p>
                                    )}

                                    {(opportunity.email || opportunity.phone) && (
                                        <div className="opportunity-contact">
                                            {opportunity.email && (
                                                <div>
                                                    <Mail size={17} />
                                                    <a href={`mailto:${opportunity.email}`}>
                                                        {opportunity.email}
                                                    </a>
                                                </div>
                                            )}

                                            {opportunity.phone && (
                                                <div>
                                                    <Phone size={17} />
                                                    <a
                                                        href={`tel:${opportunity.phone.replace(
                                                            /[^+\d]/g,
                                                            ""
                                                        )}`}
                                                    >
                                                        {opportunity.phone}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="opportunity-note">
                                        <FileText size={18} />
                                        <span>
                                            {item.tocFileName
                                                ? `Reference document available: ${item.tocFileName}.`
                                                : "Check the training details and confirm document availability with the company."}
                                        </span>
                                    </div>

                                    {opportunity.email ? (
                                        <a
                                            className="opportunity-apply"
                                            href={`mailto:${opportunity.email}?subject=${encodeURIComponent(
                                                applicationSubject
                                            )}&body=${encodeURIComponent(
                                                `Hello ${opportunity.company},\n\nI am interested in the ${opportunity.title} training opportunity. Please find my updated trainer profile attached.\n\nRegards`
                                            )}`}
                                        >
                                            Share Trainer Profile <Send size={17} />
                                        </a>
                                    ) : (
                                        <button
                                            type="button"
                                            className="opportunity-apply"
                                            onClick={() =>
                                                setError(
                                                    "Contact details are not available for this opportunity yet."
                                                )
                                            }
                                        >
                                            Share Trainer Profile <Send size={17} />
                                        </button>
                                    )}
                                </article>
                            );
                        })}

                    </div>
                </section>

                <article className="opportunity-card">
                    <div className="opportunity-card-top">
                        <div className="opportunity-company-icon">
                            <BriefcaseBusiness size={27} />
                        </div>

                        <div className="opportunity-heading">
                            <span className="opportunity-type">Corporate Training</span>
                            <h3>{opportunity.title}</h3>
                            <p>{opportunity.company}</p>
                        </div>

                        <span className="opportunity-status">Trainer Required</span>
                    </div>

                    <div className="opportunity-details">
                        <div className="opportunity-detail">
                            <MapPin />
                            <div>
                                <span>Location</span>
                                <strong>{opportunity.location}</strong>
                                <small>{opportunity.locationNote}</small>
                            </div>
                        </div>

                        <div className="opportunity-detail">
                            <Laptop />
                            <div>
                                <span>Training Mode</span>
                                <strong>{opportunity.mode}</strong>
                            </div>
                        </div>

                        <div className="opportunity-detail">
                            <CalendarDays />
                            <div>
                                <span>Start Date</span>
                                <strong>30 October 2026</strong>
                            </div>
                        </div>

                        <div className="opportunity-detail">
                            <Clock />
                            <div>
                                <span>Duration</span>
                                <strong>{opportunity.duration}</strong>
                            </div>
                        </div>

                        <div className="opportunity-detail">
                            <Users />
                            <div>
                                <span>Experience Required</span>
                                <strong>{opportunity.experience}</strong>
                            </div>
                        </div>

                        <div className="opportunity-detail">
                            <IndianRupee />
                            <div>
                                <span>Budget</span>
                                <strong>{opportunity.budget}</strong>
                                <small>TFA: {opportunity.tfa}</small>
                            </div>
                        </div>
                    </div>

                    <div className="opportunity-skills">
                        <strong>Required Skills</strong>
                        <span>{opportunity.skills}</span>
                    </div>

                    <div className="opportunity-contact">
                        <div>
                            <Mail size={17} />
                            <a href={`mailto:${opportunity.email}`}>
                                {opportunity.email}
                            </a>
                        </div>
                        <div>
                            <Phone size={17} />
                            <a href="tel:9202364862">{opportunity.phone}</a>
                        </div>
                    </div>

                    <div className="opportunity-note">
                        <FileText size={18} />
                        <span>
                            A TOC is mentioned as attached for reference. Add the actual
                            attachment or a verified document link before publishing.
                        </span>
                    </div>

                    <a
                        className="opportunity-apply"
                        href={`mailto:${opportunity.email}?subject=${encodeURIComponent(
                            "Application – Corporate Trainer Requirement – AI Developer"
                        )}&body=${encodeURIComponent(
                            "Hello BitNestTech,\n\nI am interested in the AI Developer corporate training opportunity. Please find my updated trainer profile attached.\n\nRegards"
                        )}`}
                    >
                        Share Trainer Profile <Send size={17} />
                    </a>
                </article>
            </section>

            <section className="training-section requirement-section" id="post-requirement">
                <div className="training-section-heading">
                    <div>
                        <span className="training-eyebrow">FOR COMPANIES & PARTNERS</span>
                        <h2>Post a Training Requirement</h2>
                        <p>
                            Enter the details of the training assignment and the type of
                            trainer you need.
                        </p>
                    </div>
                </div>

                <form className="requirement-form" onSubmit={handleSubmit}>
                    <div className="form-group-heading">
                        <span>01</span>
                        <div>
                            <h3>Company & Contact Details</h3>
                            <p>Tell us who is posting this requirement.</p>
                        </div>
                    </div>

                    <div className="requirement-grid">
                        <label>
                            Company / Organization Name *
                            <input
                                name="companyName"
                                value={form.companyName}
                                onChange={updateField}
                                placeholder="Enter company name"
                                required
                            />
                        </label>

                        <label>
                            Contact Person *
                            <input
                                name="contactPerson"
                                value={form.contactPerson}
                                onChange={updateField}
                                placeholder="Enter contact person's name"
                                required
                            />
                        </label>

                        <label>
                            Official Email *
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={updateField}
                                placeholder="name@company.com"
                                required
                            />
                        </label>

                        <label>
                            Contact Number *
                            <input
                                type="tel"
                                name="phone"
                                value={form.phone}
                                onChange={updateField}
                                placeholder="Enter contact number"
                                minLength={10}
                                required
                            />
                        </label>
                    </div>

                    <div className="form-divider" />

                    <div className="form-group-heading">
                        <span>02</span>
                        <div>
                            <h3>Training Details</h3>
                            <p>Describe the assignment and trainer requirements.</p>
                        </div>
                    </div>

                    <div className="requirement-grid">
                        <label className="field-full">
                            Training Title *
                            <input
                                name="trainingTitle"
                                value={form.trainingTitle}
                                onChange={updateField}
                                placeholder="e.g. Corporate Trainer Requirement – AI Developer"
                                required
                            />
                        </label>

                        <label className="field-full">
                            Required Skills *
                            <input
                                name="skills"
                                value={form.skills}
                                onChange={updateField}
                                placeholder="e.g. AI Development, Python, Machine Learning"
                                required
                            />
                        </label>

                        <label>
                            Training Location *
                            <input
                                name="location"
                                value={form.location}
                                onChange={updateField}
                                placeholder="City, State"
                                required
                            />
                        </label>

                        <label>
                            Training Mode *
                            <select
                                name="mode"
                                value={form.mode}
                                onChange={updateField}
                                required
                            >
                                <option>Offline / Classroom</option>
                                <option>Online</option>
                                <option>Hybrid</option>
                            </select>
                        </label>

                        <label>
                            Expected Start Date *
                            <input
                                type="date"
                                name="startDate"
                                value={form.startDate}
                                onChange={updateField}
                                required
                            />
                        </label>

                        <label>
                            Duration *
                            <input
                                name="duration"
                                value={form.duration}
                                onChange={updateField}
                                placeholder="e.g. 30 days"
                                required
                            />
                        </label>

                        <label>
                            Minimum Trainer Experience *
                            <input
                                name="trainerExperience"
                                value={form.trainerExperience}
                                onChange={updateField}
                                placeholder="e.g. 8+ years"
                                required
                            />
                        </label>

                        <label>
                            Budget Amount
                            <input
                                type="number"
                                min="0"
                                name="budget"
                                value={form.budget}
                                onChange={updateField}
                                placeholder="Enter amount"
                            />
                        </label>

                        <label>
                            Budget Type *
                            <select
                                name="budgetType"
                                value={form.budgetType}
                                onChange={updateField}
                                required
                            >
                                <option>Monthly</option>
                                <option>Daily</option>
                                <option>Hourly</option>
                                <option>Fixed Project</option>
                                <option>Negotiable</option>
                            </select>
                        </label>

                        <label>
                            Travel, Food & Accommodation (TFA) *
                            <select
                                name="tfa"
                                value={form.tfa}
                                onChange={updateField}
                                required
                            >
                                <option>Not Covered</option>
                                <option>Covered</option>
                                <option>Partially Covered</option>
                                <option>To Be Discussed</option>
                            </select>
                        </label>

                        <label className="field-full">
                            Requirement Description
                            <textarea
                                name="description"
                                value={form.description}
                                onChange={updateField}
                                rows={4}
                                placeholder="Describe the training objectives, audience, batch size, and other expectations."
                            />
                        </label>

                        <label className="field-full">
                            Attach TOC / Training Brief (PDF, DOC, DOCX)
                            <div className="toc-upload">
                                <Upload size={23} />
                                <span>
                                    {tocFile
                                        ? tocFile.name
                                        : "Choose a file containing the syllabus or training outline"}
                                </span>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(event) =>
                                        setTocFile(event.target.files?.[0] || null)
                                    }
                                />
                            </div>
                        </label>
                    </div>

                    {error && <p className="form-message form-error">{error}</p>}

                    {message && (
                        <p className="form-message form-success">
                            <CheckCircle2 size={18} />
                            {message}
                        </p>
                    )}

                    <div className="requirement-submit-row">
                        <p>
                            Fields marked with * are required. Your requirement should be
                            reviewed before it is published to trainers.
                        </p>
                        <button type="submit" disabled={submitting}>
                            {submitting ? "Submitting..." : "Submit Requirement"}
                            <Send size={17} />
                        </button>
                    </div>
                </form>
            </section>
        </main>
    );
}
